'use client'

import { useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'

import { ChevronDown, Search } from 'lucide-react'

import { formatCompactPublishDate } from '@/lib/blog/dates'
import { Button } from '@/components/ui/button'

import { BrowseAllStoriesButton } from './browse-all-stories-button'

/** Rows revealed per "see more" press — also the initial visible count. */
const BATCH_SIZE = 7

/** One row of the index: what the table shows, resolved server-side. */
export interface AllStoryRow {
    slug: string
    title: string
    publishedAt: string
    /** The post's primary tag label — the Category column. */
    category: string
}

interface BlogAllStoriesProps {
    /** Every post the blog serves, newest first. */
    rows: AllStoryRow[]
}

/**
 * The all-stories index at the foot of the blog homepage (blog FR-13a, RD-18):
 * every post as a row of publication date, category, and a plain followed link.
 *
 * **Every row is rendered, unconditionally.** The search box and the "see more"
 * control change only which rows are *visible* — a row that fails the filter or
 * sits past the reveal cutoff is hidden, never unmounted. The section exists to
 * give each post an inbound link from an indexed page, so anything that removed
 * a row from the markup would silently drop exactly the posts it hid. Coverage
 * over the real corpus is enforced by the route's test (blog AC-54).
 */
export const BlogAllStories = ({ rows }: BlogAllStoriesProps) => {
    const t = useTranslations('blogHome.allStories')
    const [query, setQuery] = useState('')
    const [revealed, setRevealed] = useState(BATCH_SIZE)

    // Which rows are on screen: the filter matches, capped at the reveal count.
    // Membership drives a `hidden` attribute — never a slice of `rows` — so the
    // markup keeps all 460+ anchors whatever the reader types.
    const { visible, matchCount } = useMemo(() => {
        const needle = query.trim().toLowerCase()
        const matched = needle
            ? rows.filter(
                  (row) =>
                      row.title.toLowerCase().includes(needle) ||
                      row.category.toLowerCase().includes(needle),
              )
            : rows
        return {
            visible: new Set(matched.slice(0, revealed).map((row) => row.slug)),
            matchCount: matched.length,
        }
    }, [rows, query, revealed])

    if (rows.length === 0) {
        return null
    }

    return (
        <section
            aria-label={t('heading')}
            className="mx-auto w-full max-w-[1400px] border-t border-border px-4 py-10 md:px-10 md:py-14"
        >
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-text-primary md:text-[28px]">
                        {t('heading')}
                    </h2>
                    <p className="mt-1.5 text-[15px] leading-normal text-text-secondary">
                        {t('subheading')}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative">
                        <Search
                            aria-hidden="true"
                            className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-text-secondary"
                        />
                        <input
                            type="search"
                            value={query}
                            onChange={(event) => {
                                setQuery(event.target.value)
                                // A new search starts from the top of its own
                                // result set, not the previous one's depth.
                                setRevealed(BATCH_SIZE)
                            }}
                            aria-label={t('searchLabel')}
                            placeholder={t('searchPlaceholder')}
                            className="h-9 w-full rounded-md border border-border bg-background pr-3 pl-9 text-[15px] text-text-primary placeholder:text-text-secondary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none sm:w-64"
                        />
                    </div>
                    {/* Entry to the browsable tile view (FR-13b) — a client island. */}
                    <BrowseAllStoriesButton />
                </div>
            </div>

            <p aria-live="polite" className="sr-only">
                {t('resultCount', { count: matchCount })}
            </p>

            <div className="mt-6 overflow-x-auto">
                <table className="w-full border-collapse text-left">
                    <thead>
                        <tr className="border-b border-border/60">
                            <th className="w-[8.5rem] py-2.5 pr-4 text-xs font-semibold tracking-wider text-text-secondary uppercase">
                                {t('columnDate')}
                            </th>
                            <th className="hidden w-56 py-2.5 pr-4 text-xs font-semibold tracking-wider text-text-secondary uppercase sm:table-cell">
                                {t('columnCategory')}
                            </th>
                            <th className="py-2.5 text-xs font-semibold tracking-wider text-text-secondary uppercase">
                                {t('columnTitle')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={row.slug}
                                hidden={!visible.has(row.slug)}
                                className="border-b border-border/60 last:border-b-0"
                            >
                                <td className="py-2.5 pr-4 align-top text-[15px] whitespace-nowrap text-text-secondary tabular-nums">
                                    {formatCompactPublishDate(row.publishedAt)}
                                </td>
                                <td className="hidden py-2.5 pr-4 align-top text-[15px] text-text-secondary sm:table-cell">
                                    {row.category}
                                </td>
                                <td className="py-2.5 align-top">
                                    <a
                                        href={`/blog/${row.slug}/`}
                                        className="text-[15px] leading-snug text-text-primary transition-colors hover:text-text-link hover:underline"
                                    >
                                        {row.title}
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {matchCount === 0 && (
                <p className="py-8 text-center text-[15px] text-text-secondary">
                    {t('noResults', { query: query.trim() })}
                </p>
            )}

            {revealed < matchCount && (
                <Button
                    variant="outline"
                    onClick={() => setRevealed((count) => count + BATCH_SIZE)}
                    className="mt-3 h-11 w-full"
                >
                    {t('seeMore')}
                    <ChevronDown aria-hidden="true" className="size-4" />
                </Button>
            )}
        </section>
    )
}
